<template>
  <Stack class="crm-migration-results">
    <div class="tw-position-relative">
      <Button
        @click="emit('back-to-proposal')"
        class="back-button"
        icon-before="fas fa-arrow-left"
        spacing="tight"
        size="3"
      >
        Back to Proposal
      </Button>
    </div>

    <Title>CRM Migration Results</Title>
    <p class="tw-text-gray-600 tw-mb-4">
      Results of your {{ crmType.toUpperCase() }} migration with detailed field-by-field status.
    </p>

    <BoxLoader v-if="isLoading" />

    <div v-else-if="!migrationResults">
      No migration results available.
    </div>

    <template v-else>
      <!-- Summary Section -->
      <div class="result-summary-banner" :class="summaryBannerClass">
        <div class="summary-content">
          <div class="summary-status">
            <Icon :icon="summaryIcon" class="tw-mr-3" />
            <div>
              <h2 class="summary-title">{{ summaryTitle }}</h2>
              <p class="summary-message">{{ migrationResults.message }}</p>
            </div>
          </div>
          <div class="summary-stats">
            <div v-if="migrationResults.records_created?.length > 0" class="stat-item success">
              <span class="stat-number">{{ migrationResults.records_created.length }}</span>
              <span class="stat-label">Created</span>
            </div>
            <div v-if="migrationResults.records_updated?.length > 0" class="stat-item info">
              <span class="stat-number">{{ migrationResults.records_updated.length }}</span>
              <span class="stat-label">Updated</span>
            </div>
            <div v-if="migrationResults.records_failed?.length > 0" class="stat-item danger">
              <span class="stat-number">{{ migrationResults.records_failed.length }}</span>
              <span class="stat-label">Failed</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Field Results Display -->
      <div class="rows">
        <template v-for="item in allItemsWithStatus">
          <div
            v-if="item.type === 'parent'"
            :key="`${item.id}-parent`"
            class="row is-parent"
          >
            <div class="row-steps">
              <Icon :icon="getCRMObjectIcon(item.objectType)" class="tw-mr-2" />
              <span class="row-step-label">
                {{ item.objectType }}
              </span>
              <span class="tw-text-sm tw-text-gray-500 tw-ml-2">
                ({{ item.fieldCount }} fields)
              </span>
              <div class="parent-status-summary tw-ml-4">
                <Badge v-if="item.successCount > 0" status="success" class="tw-mr-1">
                  {{ item.successCount }} success
                </Badge>
                <Badge v-if="item.failedCount > 0" status="danger">
                  {{ item.failedCount }} failed
                </Badge>
              </div>
            </div>
          </div>

          <div
            v-else
            :key="`${item.id}-element`"
            class="row is-element"
            :class="{
              [`type-${item.status}`]: item.status,
            }"
          >
            <div class="row-body">
              <span class="row-path">{{ item.fieldLabel }}</span>
              <div class="row-badge">
                <Badge :status="badgeStatus(item.status)">
                  {{ item.status }}
                </Badge>
              </div>
              <div class="current-value">
                <input
                  type="text"
                  class="stk-input row-input is-fake"
                  :class="{ 'is-empty': !item.originalValue }"
                  :value="item.originalValue || 'None'"
                  readonly
                >
              </div>
              <div class="row-direction">
                <Icon icon="fas fa-arrow-right" />
              </div>
              <div class="final-value">
                <input
                  type="text"
                  class="stk-input row-input is-fake"
                  :class="{ 
                    'is-empty': !item.finalValue,
                    'is-success': item.status === 'success',
                    'is-failed': item.status === 'failed'
                  }"
                  :value="item.finalValue || 'None'"
                  readonly
                >
              </div>
              <div class="row-status">
                <Icon 
                  :icon="getStatusIcon(item.status)" 
                  :class="getStatusIconClass(item.status)"
                />
              </div>
              
              <!-- Error/Success Details -->
              <div v-if="item.status === 'failed' && item.errorMessage" class="row-error">
                <div class="error-content">
                  <Icon icon="fas fa-exclamation-triangle" class="error-icon" />
                  <div class="error-details">
                    <div class="error-title">Failed to update</div>
                    <div class="error-message">{{ item.errorMessage }}</div>
                    <div v-if="item.fieldErrors?.length > 0" class="field-errors">
                      <div v-for="fieldError in item.fieldErrors" :key="fieldError.field" class="field-error">
                        <strong>{{ fieldError.field }}:</strong> {{ fieldError.message }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div v-else-if="item.status === 'success'" class="row-success">
                <div class="success-content">
                  <Icon icon="fas fa-check-circle" class="success-icon" />
                  <div class="success-details">
                    <div class="success-title">
                      {{ item.wasCreated ? 'Successfully created' : 'Successfully updated' }}
                    </div>
                    <div v-if="item.recordId" class="success-record-id">
                      Record ID: {{ item.recordId }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- Action Buttons -->
      <div class="action-buttons-container">
        <div class="action-buttons">
          <Button
            v-if="hasFailures"
            variant="warning"
            size="4"
            icon-before="fas fa-edit"
            @click="goToMappingEditor"
          >
            Fix CRM Mapping
          </Button>
          <Button
            variant="secondary"
            size="4"
            icon-before="fas fa-download"
            @click="downloadFailureReport"
          >
            Download Report
          </Button>
          <Button
            v-if="hasFailures"
            variant="primary"
            size="4"
            icon-before="fas fa-sync-alt"
            @click="retryMigration"
          >
            Retry Migration
          </Button>
        </div>
      </div>
    </template>
  </Stack>
</template>

<script setup>
import { ref, computed } from 'vue';
import { objectIsEmpty, prettifyMixedCase } from '@frontend/shared/Util.js';
import { apiFetch } from '@frontend/api/api.js';

import { Badge, BoxLoader, Icon, Title, Stack, Button } from '@stakeui';

// Component setup
const props = defineProps({
  entityExternalId: {
    type: String,
    required: true,
  },
  crmType: {
    type: String,
    default: 'salesforce',
  },
  migrationResults: {
    type: Object,
    required: true,
  },
  originalProposal: {
    type: Object,
    required: true,
  },
});

// Emits for handling actions
const emit = defineEmits(['back-to-proposal']);

// No loading state needed since data comes from props
const isLoading = ref(false);

// Computed properties for summary
const summaryBannerClass = computed(() => {
  if (!props.migrationResults) return '';
  
  switch (props.migrationResults.status) {
    case 'success':
      return 'is-success';
    case 'partial_success':
      return 'is-warning';
    case 'error':
      return 'is-danger';
    default:
      return 'is-info';
  }
});

const summaryIcon = computed(() => {
  if (!props.migrationResults) return 'fas fa-info-circle';
  
  switch (props.migrationResults.status) {
    case 'success':
      return 'fas fa-check-circle';
    case 'partial_success':
      return 'fas fa-exclamation-triangle';
    case 'error':
      return 'fas fa-times-circle';
    default:
      return 'fas fa-info-circle';
  }
});

const summaryTitle = computed(() => {
  if (!props.migrationResults) return 'Migration Status Unknown';
  
  switch (props.migrationResults.status) {
    case 'success':
      return 'Migration Completed Successfully';
    case 'partial_success':
      return 'Migration Partially Successful';
    case 'error':
      return 'Migration Failed';
    default:
      return 'Migration Status';
  }
});

const hasFailures = computed(() => {
  return props.migrationResults?.records_failed?.length > 0;
});

// Utility functions
const badgeStatus = status => {
  const STATUSES = {
    'success': 'success',
    'failed': 'danger',
    'skipped': 'info',
  };
  return STATUSES[status] || 'info';
};

const getCRMObjectIcon = objectType => {
  const icons = {
    'Contact': 'fas fa-user',
    'Account': 'fas fa-building',
    'Lead': 'fas fa-user-plus',
    'Opportunity': 'fas fa-handshake',
    'Case': 'fas fa-ticket-alt',
    'Task': 'fas fa-tasks',
    'Event': 'fas fa-calendar-alt',
  };
  return icons[objectType] || 'fas fa-database';
};

const getStatusIcon = status => {
  switch (status) {
    case 'success':
      return 'fas fa-check-circle';
    case 'failed':
      return 'fas fa-times-circle';
    case 'skipped':
      return 'fas fa-minus-circle';
    default:
      return 'fas fa-question-circle';
  }
};

const getStatusIconClass = status => {
  switch (status) {
    case 'success':
      return 'text-success';
    case 'failed':
      return 'text-danger';
    case 'skipped':
      return 'text-info';
    default:
      return 'text-muted';
  }
};

// Main computed property to combine all migration results into a unified view
const allItemsWithStatus = computed(() => {
  if (!props.migrationResults || !props.originalProposal.proposed_crm_data) {
    return [];
  }

  const result = [];
  const processedParents = new Set();

  // Create a mapping of successful records by object type and record ID
  const successfulRecords = new Map();
  (props.migrationResults.records_created || []).forEach(record => {
    const key = `${record.object_type}_created`;
    if (!successfulRecords.has(key)) {
      successfulRecords.set(key, []);
    }
    successfulRecords.get(key).push(record);
  });

  (props.migrationResults.records_updated || []).forEach(record => {
    const key = `${record.object_type}_updated`;
    if (!successfulRecords.has(key)) {
      successfulRecords.set(key, []);
    }
    successfulRecords.get(key).push(record);
  });

  // Create a mapping of failed records by object type
  const failedRecords = new Map();
  (props.migrationResults.records_failed || []).forEach(record => {
    const key = record.object_type;
    if (!failedRecords.has(key)) {
      failedRecords.set(key, []);
    }
    failedRecords.get(key).push(record);
  });

  // Group items by their parent identifier
  const itemsByParentPath = {};

  // ONLY process fields that were actually migrated (from actualMigratedData)
  Object.entries(props.originalProposal.proposed_crm_data || {}).forEach(([objectType, data]) => {
    // Handle arrays of records (like Asset records from arrays)
    if (Array.isArray(data)) {
      data.forEach((record, recordIndex) => {
        Object.entries(record).forEach(([fieldName, proposedValue]) => {
          const parentPath = `${objectType}_${recordIndex}`;
          const fieldId = `${parentPath}.${fieldName}`;
          
          if (!itemsByParentPath[parentPath]) {
            itemsByParentPath[parentPath] = [];
          }

          // Determine the status of this field
          const fieldStatus = determineFieldStatus(objectType, recordIndex, fieldName, proposedValue);

          // Skip records with 'skipped' status
          if (fieldStatus.status === 'skipped') {
            return;
          }

          itemsByParentPath[parentPath].push({
            id: fieldId,
            objectType,
            recordIndex,
            fieldName,
            fieldLabel: `${prettifyMixedCase(fieldName)} (Record ${recordIndex + 1})`,
            originalValue: props.originalProposal.current_crm_data?.[objectType]?.[recordIndex]?.[fieldName],
            finalValue: fieldStatus.finalValue,
            status: fieldStatus.status,
            errorMessage: fieldStatus.errorMessage,
            fieldErrors: fieldStatus.fieldErrors,
            recordId: fieldStatus.recordId,
            wasCreated: fieldStatus.wasCreated,
          });
        });
      });
    } 
    // Handle single record objects (like Contact, Account)
    else if (data && typeof data === 'object') {
      Object.entries(data).forEach(([fieldName, proposedValue]) => {
        const parentPath = objectType;
        const fieldId = `${parentPath}.${fieldName}`;
        
        if (!itemsByParentPath[parentPath]) {
          itemsByParentPath[parentPath] = [];
        }

        // Determine the status of this field
        const fieldStatus = determineFieldStatus(objectType, undefined, fieldName, proposedValue);

        // Skip records with 'skipped' status
        if (fieldStatus.status === 'skipped') {
          return;
        }

        itemsByParentPath[parentPath].push({
          id: fieldId,
          objectType,
          fieldName,
          fieldLabel: prettifyMixedCase(fieldName),
          originalValue: props.originalProposal.current_crm_data?.[objectType]?.[fieldName],
          finalValue: fieldStatus.finalValue,
          status: fieldStatus.status,
          errorMessage: fieldStatus.errorMessage,
          fieldErrors: fieldStatus.fieldErrors,
          recordId: fieldStatus.recordId,
          wasCreated: fieldStatus.wasCreated,
        });
      });
    }
  });

  // Sort parent paths for consistent ordering
  const sortedParentPaths = Object.keys(itemsByParentPath).sort((a, b) => {
    const parseParent = parentPath => {
      const parts = parentPath.split('_');
      const objectType = parts[0];
      const recordIndex = parts.length > 1 ? parseInt(parts[1]) : -1;
      return { objectType, recordIndex };
    };
    
    const aInfo = parseParent(a);
    const bInfo = parseParent(b);
    
    if (aInfo.objectType !== bInfo.objectType) {
      return aInfo.objectType.localeCompare(bInfo.objectType);
    }
    
    return aInfo.recordIndex - bInfo.recordIndex;
  });

  // Process parent paths in order
  sortedParentPaths.forEach(parentPath => {
    if (!processedParents.has(parentPath)) {
      processedParents.add(parentPath);

      const parts = parentPath.split('_');
      const objectType = parts[0];
      const recordIndex = parts.length > 1 ? parseInt(parts[1]) : undefined;

      const parentDisplayName = recordIndex !== undefined 
        ? `${objectType} (Record ${recordIndex + 1})`
        : objectType;

      // Calculate success/failure counts for this parent
      const fields = itemsByParentPath[parentPath];
      const successCount = fields.filter(f => f.status === 'success').length;
      const failedCount = fields.filter(f => f.status === 'failed').length;

      // Only show parent if it has visible fields (non-skipped)
      if (fields.length > 0) {
        result.push({
          id: `${parentPath}_parent`,
          type: 'parent',
          objectType: parentDisplayName,
          fieldCount: fields.length,
          successCount,
          failedCount,
        });

      // Add all fields for this parent, sorted by field name
      const sortedFields = fields.sort((a, b) =>
        a.fieldName.localeCompare(b.fieldName)
      );

        sortedFields.forEach(item => {
          result.push(item);
        });
      }
    }
  });

  return result;
});

// Helper function to determine the status of a specific field
const determineFieldStatus = (objectType, recordIndex, fieldName, proposedValue) => {
  // First check if this field failed specifically
  const failedRecords = props.migrationResults.records_failed || [];
  const failedRecord = failedRecords.find(record => {
    if (record.object_type !== objectType) return false;
    
    // Check if this field was in the failed data
    return record.failed_data && Object.keys(record.failed_data).includes(fieldName);
  });

  if (failedRecord) {
    // Extract field-specific errors
    const fieldErrors = (failedRecord.field_errors || []).filter(error => 
      error.field === fieldName
    );

    return {
      status: 'failed',
      finalValue: failedRecord.failed_data[fieldName],
      errorMessage: failedRecord.error_message,
      fieldErrors: fieldErrors,
    };
  }

  // Check if the record was created successfully
  const createdRecords = props.migrationResults.records_created || [];
  const createdRecord = createdRecords.find(record => {
    return record.object_type === objectType;
  });

  if (createdRecord) {
    // If the record was created and this field wasn't in the failed data, 
    // then this field was successfully created
    return {
      status: 'success',
      finalValue: proposedValue,
      recordId: createdRecord.record_id,
      wasCreated: true,
    };
  }

  // Check if the record was updated successfully
  const updatedRecords = props.migrationResults.records_updated || [];
  const updatedRecord = updatedRecords.find(record => {
    return record.object_type === objectType;
  });

  if (updatedRecord) {
    // If the record was updated and this field wasn't in the failed data,
    // then this field was successfully updated
    return {
      status: 'success',
      finalValue: proposedValue,
      recordId: updatedRecord.record_id,
      wasCreated: false,
    };
  }

  // If we can't find the field in any result, it was likely skipped
  return {
    status: 'skipped',
    finalValue: proposedValue,
  };
};

// Navigation and action functions
const goToMappingEditor = () => {
  window.location.href = `/ui/advisor/crm-mapping`;
};

const downloadFailureReport = () => {
  if (!props.migrationResults) return;
  
  const report = {
    migration_timestamp: new Date().toISOString(),
    entity_id: props.entityExternalId,
    crm_type: props.crmType,
    summary: {
      status: props.migrationResults.status,
      records_created: props.migrationResults.records_created?.length || 0,
      records_updated: props.migrationResults.records_updated?.length || 0,
      records_failed: props.migrationResults.records_failed?.length || 0,
    },
    detailed_results: props.migrationResults,
    field_level_results: allItemsWithStatus.value.filter(item => item.type !== 'parent'),
  };
  
  const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `crm-migration-results-${props.entityExternalId}-${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const retryMigration = () => {
  emit('back-to-proposal');
};
</script>

<style scoped>
.back-button {
  left: 0.75rem;
  top: 0.75rem;
  position: absolute;
}

.crm-migration-results {
  font-family: "Inter", "Inter Variable", sans-serif;
  padding: 2em;
  position: relative;
}

/* Summary Banner */
.result-summary-banner {
  border-radius: 0.75rem;
  padding: 1.5rem;
  margin-bottom: 2rem;
  border-left: 4px solid;
}

.result-summary-banner.is-success {
  background-color: #f0f9ff;
  border-left-color: #22c55e;
  color: #166534;
}

.result-summary-banner.is-warning {
  background-color: #fefce8;
  border-left-color: #eab308;
  color: #854d0e;
}

.result-summary-banner.is-danger {
  background-color: #fef2f2;
  border-left-color: #ef4444;
  color: #991b1b;
}

.result-summary-banner.is-info {
  background-color: #f0f9ff;
  border-left-color: #3b82f6;
  color: #1e40af;
}

.summary-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.summary-status {
  display: flex;
  align-items: center;
}

.summary-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
}

.summary-message {
  margin: 0;
  opacity: 0.8;
}

.summary-stats {
  display: flex;
  gap: 1rem;
}

.stat-item {
  text-align: center;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  min-width: 60px;
}

.stat-item.success {
  background-color: rgba(34, 197, 94, 0.1);
  color: #166534;
}

.stat-item.info {
  background-color: rgba(59, 130, 246, 0.1);
  color: #1e40af;
}

.stat-item.danger {
  background-color: rgba(239, 68, 68, 0.1);
  color: #991b1b;
}

.stat-number {
  display: block;
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1;
}

.stat-label {
  display: block;
  font-size: 0.75rem;
  text-transform: uppercase;
  font-weight: 500;
  margin-top: 0.25rem;
}

/* Rows styling similar to original */
.rows {
  --gap: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: var(--gap);
}

.row {
  --size: 2rem;
  --spacing: 0.25em;
  align-items: center;
  display: grid;
  gap: var(--gap);
  line-height: 1;
  grid-template-columns: 1fr;
}

.row.is-parent {
  grid-template-columns: 1fr;
  padding-top: 1em;
}

.row.is-parent:first-of-type {
  padding-top: 0;
}

.row-steps {
  border-radius: 0.5em;
  display: flex;
  align-items: center;
  gap: var(--gap);
  color: var(--stk-text-strong);
  font-weight: 600;
}

.parent-status-summary {
  display: flex;
  gap: 0.5rem;
}

.row-body {
  background-color: var(--stk-background-alt);
  border-radius: 0.5em;
  align-items: stretch;
  display: grid;
  gap: var(--gap);
  grid-template-columns: 1fr 10em 20em 1.25em 20em 2.5em;
  padding: 0.5em;
  grid-template-rows: auto auto;
}

.row-body > .row-path,
.row-body > .row-badge,
.row-body > .current-value,
.row-body > .row-direction,
.row-body > .final-value,
.row-body > .row-status {
  grid-row: 1;
  align-self: center;
}

.row-body > .row-error,
.row-body > .row-success {
  grid-row: 2;
  grid-column: 1 / -1;
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid var(--stk-border);
}

.row-path {
  padding-left: 0.5em;
  font-weight: 500;
}

.row-badge {
  text-transform: capitalize;
}

.row-direction {
  color: var(--stk-primary);
}

.current-value,
.final-value {
  display: flex;
}

.current-value {
  justify-content: end;
}

.row-input {
  width: 100%;
}

.row-input.is-fake,
.row-input.is-empty {
  background: none;
  border-color: transparent;
  box-shadow: none;
  pointer-events: none;
}

.row-input.is-fake {
  text-align: right;
  padding-left: 0;
  padding-right: 0;
}

.row-input.is-empty {
  font-style: italic;
  opacity: 0.3;
}

.row-input.is-success {
  border-color: #22c55e;
  background-color: rgba(34, 197, 94, 0.05);
}

.row-input.is-failed {
  border-color: #ef4444;
  background-color: rgba(239, 68, 68, 0.05);
}

.row-status {
  display: flex;
  justify-content: center;
  align-items: center;
}

.text-success {
  color: #22c55e;
}

.text-danger {
  color: #ef4444;
}

.text-info {
  color: #3b82f6;
}

.text-muted {
  color: #6b7280;
}

/* Error and Success Details */
.row-error,
.row-success {
  border-radius: 0.375rem;
  padding: 0.75rem;
}

.row-error {
  background-color: #fef2f2;
  border: 1px solid #fecaca;
}

.row-success {
  background-color: #f0fdf4;
  border: 1px solid #bbf7d0;
}

.error-content,
.success-content {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.error-icon {
  color: #dc2626;
  margin-top: 0.125rem;
}

.success-icon {
  color: #16a34a;
  margin-top: 0.125rem;
}

.error-title,
.success-title {
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.25rem;
}

.error-message {
  color: #6b7280;
  margin-bottom: 0.5rem;
}

.success-record-id {
  color: #6b7280;
  font-size: 0.875rem;
}

.field-errors {
  margin-top: 0.5rem;
}

.field-error {
  color: #dc2626;
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
}

/* Action Buttons */
.action-buttons-container {
  margin-top: 2rem;
  padding: 1.5rem;
  background-color: var(--stk-background-alt);
  border-radius: 0.75rem;
  border: 1px solid var(--stk-border);
}

.action-buttons {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}
</style> 
 